import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as congestionApi from '../api/congestionApi';
import * as graphApi from '../api/graphApi';
import { fetchTvWeeklyCongestion } from '../api/tvDemoApi';
import { getLocationLabel } from '../i18n';
import { TV_FOCUS_LOCATION_IDS, TV_DEMO_DEFAULT_LOCATION } from '../data/tvDemoLocations';
import { TV_STATION_ID, getTvSpotConfig } from '../data/tvDemoDisplay';
import { TV_SPOT_LOCATION_OPTIONS } from '../data/tvSpotLocation';
import { TV_BROADCAST_DEFAULT_PERIOD, TV_BROADCAST_PERIODS } from '../data/tvBroadcastPeriod';
import { TV_BROADCAST_DEFAULT_MODE, TV_BROADCAST_MODES } from '../data/tvBroadcastMode';
import { buildMockTvDemoPayload, buildMockWeeklyTvDemoPayload } from '../data/tvDemoMockData';
import { tvLog, tvWarn } from '../utils/tvDemoDebug';
import { buildDaySlots, findBusiestPartName } from '../utils/tvCongestionDisplay';
import {
  getTvDemoDataSource,
  normalizeTvWeeklyProfile,
} from '../utils/tvWeeklyFiveLevel';
import { assembleWeeklyTvDemoPayloadFromApi } from '../utils/tvWeeklyDemoTransform';
import {
  toIsoDate,
  resolveHourlyForDate,
  findCalendarDay,
  monthsSpanningRangeWithLookback,
  mergeCalendarData,
  findTodayPeak,
  buildDateTimeMatrixFromCalendar,
  buildWeeklySummary,
  buildActionScreenData,
  buildIntroPanelData,
  buildStationTodayPanel,
  buildStationTomorrowPanel,
  buildStationWeekPanel,
  buildTodayOverviewPanel,
  buildYesterdayOverviewPanel,
  buildTodaySummaryText,
} from '../utils/tvDemoDataTransform';

const META = {
  channel: '飛騨高山ケーブルネットワーク 12ch',
  segmentTitle: '高山市街 人通り予報',
};

async function fetchDtiCalendar(place, year, month, signal) {
  const res = await graphApi.getGraph(
    { place, action: 'dti_cog', year, month },
    signal,
  );
  return Array.isArray(res?.data) ? res.data : [];
}

const buildSpotPanelsForLocation = ({
  spotId,
  targetIso,
  locationHourlyMaps,
  spotCalendar,
  spotDetail,
  broadcastPeriod,
}) => {
  const spotConfig = getTvSpotConfig(spotId);
  const spotHourly = locationHourlyMaps[spotId] || {};

  return {
    today: buildStationTodayPanel({
      hourlyMap: spotHourly,
      locationName: spotConfig.label,
      locationId: spotId,
      imageSrc: spotConfig.imageSrc,
      period: broadcastPeriod,
      baseIso: targetIso,
    }),
    week: buildStationWeekPanel({
      calendarData: spotCalendar,
      detailData: spotDetail,
      baseIso: targetIso,
      locationName: spotConfig.label,
      locationId: spotId,
      imageSrc: spotConfig.imageSrc,
    }),
    tomorrow: buildStationTomorrowPanel({
      calendarData: spotCalendar,
      detailData: spotDetail,
      baseIso: targetIso,
      locationName: spotConfig.label,
      locationId: spotId,
      imageSrc: spotConfig.imageSrc,
    }),
  };
};

export const assembleTvDemoPayload = (raw, broadcastPeriod) => {
  const { targetIso, locationHourlyMaps, spotResults, sourceSummary } = raw;

  const spots = Object.fromEntries(
    TV_SPOT_LOCATION_OPTIONS.map(({ id }) => {
      const result = spotResults[id] || { calendarMerged: [], detail: null };
      return [
        id,
        buildSpotPanelsForLocation({
          spotId: id,
          targetIso,
          locationHourlyMaps,
          spotCalendar: result.calendarMerged,
          spotDetail: result.detail,
          broadcastPeriod,
        }),
      ];
    }),
  );

  const focusLocation = TV_DEMO_DEFAULT_LOCATION;
  const focusHourly = locationHourlyMaps[focusLocation];
  const peak = findTodayPeak(focusLocation, locationHourlyMaps, 'ja');
  const focusName = getLocationLabel(focusLocation, 'ja');
  const focusSlots = buildDaySlots(focusHourly);
  const busiestPart = findBusiestPartName(focusSlots);
  const usePastTense = broadcastPeriod === TV_BROADCAST_PERIODS.EVENING;
  const focusTodaySummary = buildTodaySummaryText(focusHourly, usePastTense);

  const stationHourly = locationHourlyMaps[TV_STATION_ID];
  const stationCalendar = spotResults[TV_STATION_ID]?.calendarMerged || [];
  const stationWeekly = buildDateTimeMatrixFromCalendar(stationCalendar, targetIso, 7);
  const stationWeeklySummary = buildWeeklySummary(stationWeekly, getLocationLabel(TV_STATION_ID, 'ja'));
  const stationPeak = findTodayPeak(TV_STATION_ID, locationHourlyMaps, 'ja');
  const stationTodaySummary = buildTodaySummaryText(stationHourly, usePastTense);

  const intro = buildIntroPanelData({
    segmentTitle: META.segmentTitle,
    targetIso,
    peak,
    busiestPart,
    focusHourlyMap: focusHourly,
    period: broadcastPeriod,
    stationTodayCaption: spots[TV_STATION_ID].today.caption,
    todaySummary: focusTodaySummary,
  });

  const action = buildActionScreenData({
    targetIso,
    focusLocationName: focusName,
    peak: stationPeak,
    weeklySummary: stationWeeklySummary,
    weeklyMatrix: stationWeekly,
    period: broadcastPeriod,
    todaySummary: stationTodaySummary,
  });

  const yesterdayOverview = buildYesterdayOverviewPanel({
    baseIso: targetIso,
    spotResults,
  });
  const todayOverview = buildTodayOverviewPanel({
    baseIso: targetIso,
    spotResults,
  });
  return {
    mode: 'daily',
    meta: META,
    intro,
    spots,
    todayOverview,
    yesterdayOverview,
    action,
    fetchedAt: targetIso,
    debug: { sourceSummary, broadcastPeriod, mode: 'daily' },
  };
};

/**
 * @param {string} broadcastPeriod morning|noon|evening（daily のみ使用）
 * @param {string} broadcastMode weekly|daily
 */
export function useTvDemoData(
  broadcastPeriod = TV_BROADCAST_DEFAULT_PERIOD,
  broadcastMode = TV_BROADCAST_DEFAULT_MODE,
) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rawPayload, setRawPayload] = useState(null);

  const dataSource = getTvDemoDataSource();
  const isProduction = Boolean(import.meta.env.PROD);
  const profileFromQuery = searchParams.get('thresholdProfile');
  const thresholdProfile = useMemo(() => {
    // Production ではクエリでの任意プロファイル指定を無効化
    if (isProduction) return 'tv_weekly_v1';
    const normalized = normalizeTvWeeklyProfile(profileFromQuery || 'tv_weekly_v1', {
      isProduction: false,
    });
    return normalized || 'tv_weekly_v1';
  }, [profileFromQuery, isProduction]);

  const profileQueryInvalid = useMemo(() => {
    if (isProduction || !profileFromQuery) return false;
    return normalizeTvWeeklyProfile(profileFromQuery, { isProduction: false }) == null;
  }, [profileFromQuery, isProduction]);

  const targetDate = useMemo(() => new Date(), []);
  const targetIso = useMemo(() => toIsoDate(targetDate), [targetDate]);
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth() + 1;

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const load = async () => {
      setLoading(true);
      setError('');

      tvLog('========== 取得開始 ==========', {
        targetIso,
        broadcastPeriod,
        broadcastMode,
        dataSource,
        thresholdProfile,
        year,
        month,
      });

      try {
        // 週間 + live: 専用 API（tv_weekly_*）。通常 heatmap 経路は使わない
        if (broadcastMode === TV_BROADCAST_MODES.WEEKLY && dataSource === 'live') {
          if (profileQueryInvalid) {
            setRawPayload({
              weeklyLiveError: true,
              targetIso,
              thresholdProfile,
              errorMessage: `不正な thresholdProfile です（許可: legacy / tv_weekly_v1）: ${profileFromQuery}`,
            });
            return;
          }
          const res = await fetchTvWeeklyCongestion({
            baseDate: targetIso,
            thresholdProfile,
            signal,
          });
          if (!res?.success || !res?.data) {
            throw new Error(res?.detail || res?.message || 'TV週間APIの応答が不正です');
          }
          setRawPayload({
            weeklyLive: true,
            targetIso,
            thresholdProfile,
            apiData: res.data,
          });
          tvLog('========== 週間 live 取得完了 ==========');
          return;
        }

        // 週間 + mock: カレンダー取得不要
        if (broadcastMode === TV_BROADCAST_MODES.WEEKLY && dataSource === 'mock') {
          setRawPayload({ weeklyMock: true, targetIso });
          return;
        }

        // daily モード（従来）
        const monthSpan = monthsSpanningRangeWithLookback(targetIso, 7, 7);
        const locationIdsToFetch = [...new Set([...TV_FOCUS_LOCATION_IDS, TV_STATION_ID])];

        const locationFetches = locationIdsToFetch.map(async (locId) => {
          const [detail, ...calendarChunks] = await Promise.all([
            congestionApi.getCongestionDetail(locId, {
              targetDate: targetIso,
              weeksCount: 3,
              signal,
            }),
            ...monthSpan.map(({ year: y, month: m }) =>
              fetchDtiCalendar(locId, y, m, signal),
            ),
          ]);
          const calendarMerged = mergeCalendarData(calendarChunks);
          const calendarDay = findCalendarDay(calendarMerged, targetIso);
          const { map, source } = resolveHourlyForDate(
            calendarDay,
            detail,
            locId,
            targetIso,
          );
          return { locId, detail, calendarMerged, map, source };
        });

        const locationResults = await Promise.all(locationFetches);

        const locationHourlyMaps = {};
        const sourceSummary = {};
        const spotResults = {};
        locationResults.forEach(({ locId, map, source, calendarMerged, detail }) => {
          locationHourlyMaps[locId] = map || {};
          sourceSummary[locId] = source;
          spotResults[locId] = { calendarMerged, detail };
        });

        tvLog('地点別データソース', sourceSummary);

        TV_SPOT_LOCATION_OPTIONS.forEach(({ id }) => {
          if (!spotResults[id]?.calendarMerged?.length) {
            tvWarn(`${id} のカレンダーデータなし`);
          }
        });

        setRawPayload({
          targetIso,
          locationHourlyMaps,
          spotResults,
          sourceSummary,
        });
        tvLog('========== 取得完了 ==========');
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[TV-Demo] 取得エラー', err);

        // live 週間はモックへフォールバックしない（障害を隠さない）
        if (broadcastMode === TV_BROADCAST_MODES.WEEKLY && dataSource === 'live') {
          setRawPayload({
            weeklyLiveError: true,
            targetIso,
            thresholdProfile,
            errorMessage: err.message || String(err),
          });
          setError(`[develop] TV週間 live 取得失敗: ${err.message || String(err)}`);
          return;
        }

        setRawPayload({ mock: true, targetIso });
        setError(
          err.message?.includes('timeout') || err.message?.includes('Failed to fetch')
            ? 'APIに接続できないためモックデータを表示しています。'
            : `取得エラー（モック表示）: ${err.message}`,
        );
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIso, year, month, broadcastMode, dataSource, thresholdProfile, profileQueryInvalid]);

  const data = useMemo(() => {
    if (!rawPayload) return null;

    if (rawPayload.weeklyLiveError) {
      return null;
    }

    if (broadcastMode === TV_BROADCAST_MODES.WEEKLY) {
      if (rawPayload.weeklyLive && rawPayload.apiData) {
        return assembleWeeklyTvDemoPayloadFromApi(rawPayload.apiData, {
          profile: rawPayload.thresholdProfile || thresholdProfile,
        });
      }
      // mock（既定）
      return buildMockWeeklyTvDemoPayload(rawPayload.targetIso || targetIso);
    }

    if (rawPayload.mock) {
      return buildMockTvDemoPayload(broadcastPeriod);
    }
    return assembleTvDemoPayload(rawPayload, broadcastPeriod);
  }, [rawPayload, broadcastPeriod, broadcastMode, targetIso, thresholdProfile]);

  const isMock = Boolean(
    rawPayload?.mock
    || rawPayload?.weeklyMock
    || (broadcastMode === TV_BROADCAST_MODES.WEEKLY && dataSource === 'mock'),
  );

  return {
    loading,
    error,
    data,
    targetIso,
    broadcastPeriod,
    broadcastMode,
    isMock,
    dataSource,
    thresholdProfile,
  };
}

export default useTvDemoData;
